import React from 'react';

import styled, { css } from 'styled-components';

import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { IPrice, IPriceAndCalories } from './types';

const Container = styled.div<{ $condensed?: boolean }>`
  display: flex;
  justify-content: ${p => (p.$condensed ? 'flex-start' : 'space-between')};
  column-gap: ${p => (p.$condensed ? '1rem' : 'initial')};
  margin: ${p => (p.$condensed ? '0' : '1rem 0')};
`;

const redStyles = css`
  color: ${Styles.color.pricing};
  font-weight: ${Styles.fontWeight.heavy};
`;
const normalStyles = css`
  color: ${Styles.color.grey.one};
  font-weight: ${Styles.fontWeight.normal};
`;

const StyledText = styled.h5<{ red?: boolean }>`
  display: inline-block;
  margin: 0;
  ${({ red }) => (red ? redStyles : normalStyles)}
`;

const GreyStyledText = styled(StyledText)`
  color: ${Styles.color.grey.two};
`;

export const Price: React.FC<IPrice> = props => (
  <StyledText data-testid="wizard-price" red={props.red}>
    {props.children}
  </StyledText>
);

/**
 * Conditionally hide site-wide calories based on an LD flag. All rendered calories outside of
 * the nutrition explorer should be wrapped with this component
 */
export const Calories: React.FC<{ children: React.ReactNode; hideCalories?: boolean }> = ({
  children,
  hideCalories = false,
}) => {
  const hideUiCalories = useFlag(LaunchDarklyFlag.HIDE_UI_CALORIES) || hideCalories;

  if (hideUiCalories) {
    return null;
  }

  return <>{children}</>;
};

export const PriceAndCalories: React.FC<IPriceAndCalories> = ({
  isOffer,
  currentPrice,
  shouldHideCalories,
  formattedCalories,
  shouldHidePrice,
  condensedContainer = false,
}) => {
  return (
    <Container $condensed={condensedContainer}>
      {!shouldHidePrice && <Price red={isOffer}>{currentPrice}</Price>}
      {formattedCalories ? (
        <Calories hideCalories={shouldHideCalories}>
          <GreyStyledText data-testid="wizard-calories">{formattedCalories}</GreyStyledText>
        </Calories>
      ) : null}
    </Container>
  );
};
